import React, { useEffect, useState } from 'react';

interface NumberInputProps {
  value: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
  inputMode?: 'numeric' | 'decimal' | 'text';
  placeholder?: string;
  className?: string;
  /** If true, allow decimals (e.g. 6.5 ratings). Default false (integers). */
  allowDecimal?: boolean;
  /** Value to fall back to when the field is left empty on blur. */
  emptyDefault?: number;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Draft-backed numeric text input.
 *
 * Fixes the classic "controlled numeric input" bug where clearing the field
 * snaps it straight back to a number (e.g. deleting "8" instantly shows "0" and
 * you get "01" while typing). Here the displayed value is an internal string
 * draft while the field is focused, so the user can fully clear it and type a
 * fresh number cleanly. It still commits a validated, clamped number on every
 * valid keystroke (so live tallies update), and normalizes on blur.
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onCommit,
  min,
  max,
  inputMode = 'numeric',
  placeholder,
  className,
  allowDecimal = false,
  emptyDefault = 0,
  disabled,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() => String(value));

  // Keep the draft in sync with the committed value, but never while the user
  // is actively typing (that would clobber the draft and trigger the bug).
  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  const parse = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const n = allowDecimal ? parseFloat(raw) : parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  };

  const clamp = (n: number): number => {
    let v = n;
    if (min !== undefined) v = Math.max(min, v);
    if (max !== undefined) v = Math.min(max, v);
    return v;
  };

  const isValidPartial = (raw: string): boolean => {
    if (raw === '') return true;
    return allowDecimal ? /^\d*\.?\d*$/.test(raw) : /^\d+$/.test(raw);
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode={inputMode}
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value;
        // Only accept characters valid for the number type, so stray
        // letters/symbols never appear and corrupt the value.
        if (!isValidPartial(raw)) return;
        setDraft(raw);
        // Commit live on every complete number, but pause when a decimal point
        // is trailing (user is still typing the fractional part).
        if (raw !== '' && !(allowDecimal && raw.endsWith('.'))) {
          const n = parse(raw);
          if (n !== null) onCommit(clamp(n));
        }
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        // Normalize: empty -> emptyDefault (clamped), otherwise clamp + commit.
        const n = parse(draft);
        const fallback = n === null ? clamp(emptyDefault) : clamp(n);
        if (n === null || fallback !== n) setDraft(String(fallback));
        onCommit(fallback);
      }}
      className={className}
    />
  );
};